import React, { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useT } from '@/contexts/I18nContext';

import {
  type KeypointBlock,
  type KeypointBlockOld,
  type KeypointBlockNew,
  deriveKeypointClosingSeverity,
  deriveKeypointColor,
  serializeKeypointBlock,
} from './keypointBlock';

// `format` deliberately excluded — this form never switches a block between
// old/new format, only edits fields within whichever one it was parsed as.
// The intersection of the two (each partial) lets `set({ openingFast: true })`
// (old-only) and `set({ neverFilled: true })` (new-only) both type-check from
// the same helper without a full discriminated-union patch dance.
type KeypointPatch = Partial<Omit<KeypointBlockOld, 'format'>> & Partial<Omit<KeypointBlockNew, 'format'>>;

/**
 * Edits a whole keypoint category block as one unit. Reports generated before
 * 2026-09's Round-2 rewrite still carry the OLD multi-row format (fast/normal
 * opening, optional decline, closing %) and are never regenerated, so this
 * form branches on `initial.format`:
 *  - 'old' → the original fast/normal + decline + closing controls.
 *  - 'new' → "never filled all day" toggle, opening-problem checkbox with
 *    its %/time fields, closing % (+ replenishment time when the closing
 *    side is flagged), reproducing the pre-tree one-line `clarify_category`
 *    output. The circle and the closing flag are DERIVED from those numbers
 *    (deriveKeypointColor / deriveKeypointClosingSeverity, Alexey
 *    2026-09-08) — no colour or "problem" pickers.
 * Both share the same "preview" panel (serializeKeypointBlock) and Apply/Cancel
 * pattern.
 */
export function KeypointBlockForm({
  initial,
  onApply,
  onCancel,
}: {
  initial: KeypointBlock;
  onApply: (rows: string[]) => void;
  onCancel: () => void;
}) {
  const { t } = useT();
  const [b, setB] = useState<KeypointBlock>(initial);
  const set = (patch: KeypointPatch) => setB((prev) => ({ ...prev, ...patch }) as KeypointBlock);

  const previewRows = serializeKeypointBlock(b);
  // Old format: the fixed category emoji. New format: the DERIVED circle.
  const titleEmoji = b.format === 'old' ? b.emoji : b.external ? '' : deriveKeypointColor(b);
  const closingFlagged = b.format === 'new' && deriveKeypointClosingSeverity(b) > 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {titleEmoji} {b.name || t('reportValidation.kp.title')}
      </div>

      {/* External-reason case replaces the whole block */}
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={b.external}
          onChange={(e) => set({ external: e.target.checked })}
          className="size-4"
        />
        <span className="text-muted-foreground">{t('reportValidation.kp.external')}</span>
      </label>

      {!b.external && b.format === 'old' && (
        <>
          {/* Opening variant */}
          <div className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">{t('reportValidation.kp.opening')}</span>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={b.openingFast ? 'default' : 'outline'}
                size="sm"
                onClick={() => set({ openingFast: true })}
              >
                {t('reportValidation.kp.fast')}
              </Button>
              <Button
                type="button"
                variant={!b.openingFast ? 'default' : 'outline'}
                size="sm"
                onClick={() => set({ openingFast: false })}
              >
                {t('reportValidation.kp.normal')}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-muted-foreground">{t('reportValidation.tf.openPct')}</span>
              <Input
                type="number"
                min={0}
                max={100}
                value={b.openingPct}
                onChange={(e) => set({ openingPct: Number(e.target.value) })}
              />
            </label>
            {!b.openingFast && (
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-muted-foreground">{t('reportValidation.tf.normTime')}</span>
                <Input
                  type="time"
                  value={b.normalizedTime ?? ''}
                  onChange={(e) => set({ normalizedTime: e.target.value })}
                  className="w-32"
                />
              </label>
            )}
          </div>

          {/* Decline */}
          <div className="flex flex-col gap-2 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={b.declinePresent}
                onChange={(e) => set({ declinePresent: e.target.checked })}
                className="size-4"
              />
              <span className="text-muted-foreground">{t('reportValidation.kp.decline')}</span>
            </label>
            {b.declinePresent && (
              <Input
                type="time"
                value={b.declineTime ?? ''}
                onChange={(e) => set({ declineTime: e.target.value })}
                className="w-32"
              />
            )}
          </div>

          {/* Closing */}
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">{t('reportValidation.tf.closePct')}</span>
            <Input
              type="number"
              min={0}
              max={100}
              value={b.closingPct}
              onChange={(e) => set({ closingPct: Number(e.target.value) })}
              className="w-32"
            />
            {b.closingPct <= 15 && (
              <span className="text-xs text-muted-foreground/70">
                {t('reportValidation.kp.closeEmptyHint')}
              </span>
            )}
          </label>
        </>
      )}

      {!b.external && b.format === 'new' && (
        <>
          {/* No colour picker — the circle in the title is derived (deriveKeypointColor). */}

          {/* "Never reached fill threshold" fallback replaces the opening/closing pair */}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={b.neverFilled}
              onChange={(e) => set({ neverFilled: e.target.checked })}
              className="size-4"
            />
            <span className="text-muted-foreground">{t('reportValidation.kp.neverFilled')}</span>
          </label>

          {b.neverFilled ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-muted-foreground">{t('reportValidation.tf.openPct')}</span>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={b.openingPct}
                  onChange={(e) => set({ openingPct: Number(e.target.value) })}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-muted-foreground">{t('reportValidation.tf.closePct')}</span>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={b.closingPct}
                  onChange={(e) => set({ closingPct: Number(e.target.value) })}
                />
              </label>
              {/* Whether the closing value is underlined is derived from
                  closingPct + category (closing_severity_of) — no checkbox. */}
              {closingFlagged && b.closingPct <= 15 && (
                <span className="col-span-full text-xs text-muted-foreground/70">
                  {t('reportValidation.kp.closeEmptyHint')}
                </span>
              )}
            </div>
          ) : (
            <>
              {/* Opening problem */}
              <div className="flex flex-col gap-2 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={b.openingProblem}
                    onChange={(e) => set({ openingProblem: e.target.checked })}
                    className="size-4"
                  />
                  <span className="text-muted-foreground">
                    {t('reportValidation.kp.openingProblem')}
                  </span>
                </label>
                {b.openingProblem && (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label className="flex flex-col gap-1">
                      <span className="text-muted-foreground">
                        {t('reportValidation.tf.openPct')}
                      </span>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        value={b.openingPct}
                        onChange={(e) => set({ openingPct: Number(e.target.value) })}
                      />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="text-muted-foreground">
                        {t('reportValidation.tf.normTime')}
                      </span>
                      <Input
                        type="time"
                        value={b.filledAt ?? ''}
                        onChange={(e) => set({ filledAt: e.target.value })}
                        className="w-32"
                      />
                    </label>
                  </div>
                )}
              </div>

              {/* Closing — % is always shown/serialized. Whether the closing side
                  is a problem is DERIVED from that % + category; when it is, the
                  replenishment time (optional) adds the "последнее пополнение
                  около" clause. */}
              <div className="flex flex-col gap-2 text-sm">
                <label className="flex flex-col gap-1">
                  <span className="text-muted-foreground">{t('reportValidation.tf.closePct')}</span>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={b.closingPct}
                    onChange={(e) => set({ closingPct: Number(e.target.value) })}
                    className="w-32"
                  />
                </label>
                {closingFlagged && (
                  <label className="flex flex-col gap-1">
                    <span className="text-muted-foreground">{t('reportValidation.tree.replenishedAt')}</span>
                    <Input
                      type="time"
                      value={b.replenishedAt ?? ''}
                      onChange={(e) => set({ replenishedAt: e.target.value || null })}
                      className="w-32"
                    />
                  </label>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* Preview */}
      <div className="flex flex-col gap-1">
        <span className="text-xs text-muted-foreground">{t('reportValidation.form.preview')}</span>
        <div className="report-body rounded-md border border-border bg-muted/40 p-2 text-sm">
          {previewRows.map((row, i) => (
            <div key={i} dangerouslySetInnerHTML={{ __html: row }} />
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          {t('reportValidation.form.cancel')}
        </Button>
        <Button type="button" size="sm" onClick={() => onApply(serializeKeypointBlock(b))}>
          {t('reportValidation.form.apply')}
        </Button>
      </div>
    </div>
  );
}
